import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../components/Button";
import { MetaData } from "../../../helpers/MetaData";
import JsonLd from "../../../helpers/JsonLD";
import { useWindowResize } from "../../../hooks/useWindowResize";
import { handlerAddPacketInBasket, handlerDeletePacketFromBasket } from "../../../redux/Reducers/packetSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { IPacket } from "../../../types";
import { charlottePhotographyServiceAreas } from "../../../utils/seo";
import eventPhoto from "../../Resources/assets/images/small-events.webp";
import familySessionMobile from "../../Resources/assets/images/family-christmas-at-home-portrait.webp";
import maternity from "../../Resources/assets/images/maternity-studio-blush-gown.webp";
import maternitySessionMobile from "../../Resources/assets/images/maternity-sunflower-picnic-portrait.webp";
import Styles from "./newbornPricing.module.scss";
import PricingNavigation, { getPricingBreadcrumbSchema, PricingCategoryNavigation } from "./PricingNavigation";
import PricingGuideCenter from "./PricingGuideCenter";
import { socialPreviews } from "../../../utils/socialPreviews";

type PricingType = "baby" | "family" | "maternity";

interface PackageSection {
  items: string[];
  title: string;
}

interface PortraitPackage {
  cartImage: string;
  category: string;
  description: string;
  id: string;
  images: string;
  imageAlt: string;
  name: string;
  note: string;
  photo: string;
  mobilePhoto?: string;
  photoOrientation: "landscape" | "portrait";
  price: string;
  priceLabel?: string;
  sections: PackageSection[];
  style: string;
  time: string;
}

interface PageContent {
  eyebrow: string;
  lead: string;
  packages: PortraitPackage[];
  privatePrices: Array<[string, string]>;
  styleNote: string;
  title: string;
}

const received30 = [
  "10 fully retouched high-resolution images",
  "20 additional high-resolution images with color and light correction",
  "30 digital images total",
  "Private online gallery and digital download",
  "Typical delivery within 1–1.5 weeks",
];

const received20 = [
  "10 fully retouched high-resolution images",
  "20 additional high-resolution images with color and light correction",
  "30 digital images total",
  "Private online gallery and digital download",
  "Typical delivery within 1–1.5 weeks",
];

const babyPackages: PortraitPackage[] = [
  {
    id: "baby-milestone-static",
    name: "Baby — Milestone Session",
    category: "Professionally styled in-home photography",
    description: "A professionally styled in-home session for babies from 1 month through 11 months.",
    style: "Milestone, 1–11 months",
    time: "1–2 hr",
    images: "30",
    imageAlt: "Baby photographed during a professionally styled milestone session",
    price: "$350",
    photo:
      "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/desktop/baby-one-year_description_desktop.webp",
    mobilePhoto:
      "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/baby_description_mobile.webp",
    photoOrientation: "portrait",
    cartImage:
      "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/desktop/baby-one-year_description_desktop.webp",
    note: "This is a styled milestone session. I bring professional lighting, backdrops, stands, props, outfits, and accessories to your home.",
    sections: [
      {
        title: "Session includes",
        items: [
          "1–2 hour in-home session",
          "3 professionally styled baby setups",
          "2–3 outfits for the baby",
          "Backdrops, professional lighting, and necessary equipment",
          "Baskets, small beds, crates, blankets, and other age-appropriate props",
          "Outfits, hats, headbands, and accessories from my collection",
          "Personal clothing or meaningful items may be included when discussed in advance",
          "One coordinated family look with parents and siblings, upon request",
          "Additional family look or outfit change: $100",
          "Up to 6 people total included; each additional person: $30",
        ],
      },
      { title: "What you receive", items: received30 },
    ],
  },
  {
    id: "first-birthday-static",
    name: "First Birthday Session",
    category: "Styled one-year milestone photography",
    description: "A styled one-year milestone session celebrating your baby's first birthday.",
    style: "One-year milestone",
    time: "1–2 hr",
    images: "30",
    imageAlt: "Baby girl celebrating her first birthday with strawberries and balloons",
    price: "$350",
    photo: "https://cdn.alenalobacheva.com/gallery/baby/baby6.webp",
    photoOrientation: "landscape",
    cartImage: "https://cdn.alenalobacheva.com/gallery/baby/baby6.webp",
    note: "A cake smash setup may be added. Cake and balloon decor are not included in the package price and may be arranged for a custom quote or provided by the family.",
    sections: [
      {
        title: "Session includes",
        items: [
          "1–2 hour in-home session",
          "3 professionally styled baby setups",
          "2–3 outfits for the baby",
          "A first-birthday-themed setup with my Happy Birthday banner and decorative bunting",
          "Backdrops, professional lighting, and necessary equipment",
          "Baskets, small beds, crates, blankets, outfits, and accessories from my collection",
          "One coordinated family look with parents and siblings, upon request",
          "Additional family look or outfit change: $100",
          "Up to 6 people total included; each additional person: $30",
        ],
      },
      {
        title: "Cake and balloon decor",
        items: [
          "Custom cake and balloon costs vary by theme, colors, design, and complexity",
          "Families may order or prepare their own cake and balloon area",
          "All family-provided decor must be coordinated with me in advance",
        ],
      },
      { title: "What you receive", items: received30 },
    ],
  },
];

const maternityPackage: PortraitPackage = {
  id: "maternity-static",
  name: "Maternity Session",
  category: "Individual maternity portraits or maternity with family",
  description: "A one-hour portrait session created to celebrate this special chapter of your life.",
  style: "Individual or with family",
  time: "1 hr",
  images: "20",
  imageAlt: "Expectant mother during a styled maternity portrait session",
  price: "$350",
  photo: maternity,
  mobilePhoto: maternitySessionMobile,
  photoOrientation: "portrait",
  cartImage: maternity,
  note: "Choose one location: your home, an outdoor location, or a professional studio. Studio rental is not included and is quoted separately.",
  sections: [
    {
      title: "Individual maternity session",
      items: [
        "1 hour of photography",
        "Up to 4 different looks",
        "Complimentary access to 4 maternity gowns from my client closet",
        "Personal dresses and outfits are also welcome",
      ],
    },
    {
      title: "Maternity session with family",
      items: [
        "Partner and children are welcome to participate",
        "Approximately 2–3 coordinated looks because family outfit changes require more time",
        "Up to 6 people total included",
      ],
    },
    {
      title: "Home and studio sessions",
      items: [
        "Many maternity sessions can be photographed beautifully at home when space allows",
        "I can bring portable backdrops, a backdrop stand, and professional lighting",
        "The available home space will be reviewed before the session",
        "A studio is recommended when the concept requires a studio interior or specialized equipment",
      ],
    },
    { title: "What you receive", items: received20 },
  ],
};

const familyPackages: PortraitPackage[] = [
  {
    id: "family-static",
    name: "Family Session",
    category: "Relaxed family portrait photography",
    description: "A relaxed one-hour portrait session for families of up to 6 people.",
    style: "Family portraits",
    time: "1 hr",
    images: "20",
    imageAlt: "Family photographed together during a relaxed portrait session",
    price: "$350",
    photo: "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/family_description_mobile.webp",
    mobilePhoto: familySessionMobile,
    photoOrientation: "portrait",
    cartImage:
      "https://cdn.alenalobacheva.com/staticPhotosForPackets/description/mobile/family_description_mobile.webp",
    note: "Clients provide their own clothing and accessories. Wardrobe consultation is included, and I will help coordinate colors and styles before the session.",
    sections: [
      {
        title: "Session includes",
        items: [
          "1 hour of photography",
          "Approximately 2–3 coordinated family looks",
          "Final number of looks depends on family size, children, and time needed for outfit changes",
          "Family portraits for up to 6 people",
          "Wardrobe guidance before the session; clients may send photographs of planned outfits",
          "One selected location: your home, outdoors, or a professional studio",
          "Studio rental is not included and is quoted separately",
        ],
      },
      {
        title: "Home and studio sessions",
        items: [
          "Many family sessions can be photographed comfortably at home when space allows",
          "I can bring portable backdrops, a backdrop stand, and professional lighting",
          "The available home space will be reviewed before the session",
          "A studio is recommended for concepts requiring a studio interior or specialized equipment",
        ],
      },
      { title: "What you receive", items: received20 },
    ],
  },
  {
    id: "small-event-static",
    name: "Small Event Photography",
    category: "Birthdays and small celebrations",
    description:
      "Candid event coverage for birthdays, children's parties, family celebrations, and other small events.",
    style: "Up to about 50 guests",
    time: "First hour",
    images: "Full gallery",
    imageAlt: "Guests celebrating together during a small event",
    price: "$250",
    priceLabel: "From $250",
    photo: eventPhoto,
    photoOrientation: "portrait",
    cartImage: eventPhoto,
    note: "First hour: $250. Each additional hour: $150. If coverage continues for 15 minutes or more beyond the scheduled ending time, the next full hourly rate applies.",
    sections: [
      {
        title: "Event coverage",
        items: [
          "Indoor or outdoor events with approximately 50 guests or fewer",
          "Venue, decorations, guests, candid interactions, family groups, and event highlights",
          "Cake, gifts, speeches, traditions, and other important moments",
          "A brief 5–10 minute overrun will not normally be charged as an additional hour",
          "Events with more than approximately 50 guests or simultaneous coverage needs may require an additional photographer and are not included in this package",
        ],
      },
      {
        title: "What you receive",
        items: [
          "Full gallery of all successful photographs from the event",
          "High-resolution digital images",
          "Professional color and light correction, cropping, and basic adjustments",
          "Private online gallery and digital download",
          "Delivery within 1–2 weeks",
        ],
      },
      {
        title: "Editing and travel",
        items: [
          "Detailed Photoshop retouching is not included",
          "Detailed retouching for selected event photographs: $20 per image",
          "$30 travel fee for events in Charlotte and nearby areas",
          "Travel farther outside Charlotte is quoted separately based on distance",
        ],
      },
    ],
  },
];

const pageContent: Record<PricingType, PageContent> = {
  baby: {
    eyebrow: "Baby milestone photography · Charlotte, NC",
    title: "Baby Session Pricing",
    lead: "Styled milestone and first-birthday sessions created for your baby's changing personality and newest accomplishments.",
    styleNote:
      "Baby and First Birthday sessions are designed primarily as styled in-home sessions with professional lighting, backdrops, props, and accessories.",
    packages: babyPackages,
    privatePrices: [
      ["Baby — Milestone Session", "$550"],
      ["First Birthday Session", "$550"],
    ],
  },
  maternity: {
    eyebrow: "Maternity photography · Charlotte, NC",
    title: "Maternity Session Pricing",
    lead: "An elegant maternity experience designed to celebrate this season with comfort and intention.",
    styleNote:
      "Choose one location: your home, outdoors, or a professional studio. We will plan the setting, wardrobe, and number of looks around your comfort and who will participate.",
    packages: [maternityPackage],
    privatePrices: [["Maternity Session", "$550"]],
  },
  family: {
    eyebrow: "Family and event photography · Charlotte, NC",
    title: "Family & Event Photography Pricing",
    lead: "Relaxed family portraits and thoughtful coverage of the celebrations you want to remember.",
    styleNote:
      "Family portraits take place at one selected location. Small Event Photography follows a separate hourly structure and includes the full gallery of successful images.",
    packages: familyPackages,
    privatePrices: [["Family Session", "$550"]],
  },
};

const buildCartPacket = (item: PortraitPackage, type: PricingType): IPacket => ({
  _id: item.id,
  countLocations: "One selected location",
  createdAt: "",
  description: item.description,
  descriptionEN: item.sections.flatMap((section) => section.items).join("\n"),
  duration: item.time,
  getFromPhotosession: item.sections[item.sections.length - 1]?.items.join("\n") || "",
  getFromPhotosessionEN: item.sections[item.sections.length - 1]?.items.join("\n") || "",
  image: item.cartImage,
  imageDescription: item.photo,
  imageDescriptionMobile: item.photo,
  namePacket: item.name,
  photosessionType: item.id === "small-event-static" ? "event" : type,
  pinned: false,
  price: item.price.replace(/[$,]/g, ""),
  shortDescription: `${item.time === "1 hr" ? "1 hour" : item.time.replace(/\bhr\b/, "hours")} · ${
    /^\d+$/.test(item.images) ? `${item.images} digital images` : item.images
  }`,
});

const FamilyMaternityPricingPage = ({ type }: { type: PricingType }): React.ReactElement => {
  const content = pageContent[type];
  const socialPreview = socialPreviews.pricing[type];
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [removedPackageId, setRemovedPackageId] = useState<string | null>(null);
  const { packetInBasket } = useAppSelector((state) => state.packets);

  useEffect(() => {
    if (!hash) return;

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const packageElement = document.getElementById(hash.slice(1));
        packageElement?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [hash]);

  useEffect(() => {
    if (!removedPackageId) return;
    const timeoutId = window.setTimeout(() => setRemovedPackageId(null), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [removedPackageId]);
  const { width } = useWindowResize();
  const canonicalUrl = `https://alenalobacheva.com/prices/${type}/`;
  const isSinglePackage = content.packages.length === 1;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${canonicalUrl}#service`,
    name: `${
      type === "baby" ? "Baby" : type === "family" ? "Family and Small Event" : "Maternity"
    } Photography in Charlotte, NC`,
    description: content.lead,
    provider: {
      "@type": "LocalBusiness",
      "@id": "https://alenalobacheva.com/#localbusiness",
      name: "Alena Lobacheva Photography",
      telephone: "+1-516-468-4837",
    },
    areaServed: charlottePhotographyServiceAreas,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `${type} photography pricing`,
      itemListElement: content.packages.map((item) => ({
        "@type": "Offer",
        name: item.name,
        price: item.price.replace(/[$,]/g, ""),
        priceCurrency: "USD",
        url: canonicalUrl,
      })),
    },
  };

  const handlePackageAction = (item: PortraitPackage): void => {
    const isInBasket = packetInBasket.some((packet) => packet._id === item.id);
    if (isInBasket) {
      navigate("/basket");
      return;
    }
    dispatch(handlerAddPacketInBasket(buildCartPacket(item, type)));
    setRemovedPackageId(null);
  };

  const handleRemovePackage = (item: PortraitPackage): void => {
    dispatch(handlerDeletePacketFromBasket(item.id));
    setRemovedPackageId(item.id);
  };

  const renderPackageBody = (item: PortraitPackage): React.ReactElement => {
    const receiveSection = item.sections.find((section) => section.title === "What you receive");
    const informationSections = item.sections.filter((section) => section.title !== "What you receive");

    return (
      <div className={`${Styles.package__body} ${Styles.package__body_withImage}`}>
        <p className={Styles.package__description}>{item.description}</p>
        <picture
          className={`${Styles.package__picture} ${Styles[`package__picture_${item.photoOrientation}`]} ${
            item.mobilePhoto ? Styles.package__picture_mobileLandscape : ""
          }`}
        >
          {item.mobilePhoto && <source media="(max-width: 760px)" srcSet={item.mobilePhoto} />}
          <img alt={item.imageAlt} decoding="async" loading="lazy" src={item.photo} />
        </picture>
        <div className={Styles.package__lists}>
          <div className={Styles.package__informationColumn}>
            {informationSections.map((section) => (
              <section key={section.title}>
                <h3>{section.title}</h3>
                <ul>
                  {section.items.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
          <div className={Styles.package__receiveColumn}>
            {receiveSection && (
              <>
                <h3>{receiveSection.title}</h3>
                <ul>
                  {receiveSection.items.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </>
            )}
            <p className={Styles.package__note}>
              <strong>Please note.</strong> {item.note}
            </p>
            <div className={Styles.package__orderButton}>
              <Button
                styleButton="ping"
                edit
                type="button"
                editStyle={packetInBasket.some((packet) => packet._id === item.id) ? "green" : "buttonPriceDescription"}
                onClick={() => handlePackageAction(item)}
              >
                {packetInBasket.some((packet) => packet._id === item.id) ? "Go to cart" : "Add package to cart"}
              </Button>
              {packetInBasket.some((packet) => packet._id === item.id) && (
                <button
                  className={Styles.package__removeButton}
                  type="button"
                  onClick={() => handleRemovePackage(item)}
                >
                  Remove from cart
                </button>
              )}
            </div>
            {removedPackageId === item.id && (
              <p className={Styles.package__cartNotice} role="status">
                Package removed from your cart.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <MetaData
        title={`${content.title} in Charlotte, NC`}
        description={`${content.lead} Compare session details, images, locations, pricing, booking policies, and privacy options.`}
        canonicalLink={canonicalUrl}
        {...socialPreview}
        imageAltOG={`${type} photography in Charlotte, North Carolina`}
        titleOG={`${content.title} in Charlotte, NC`}
        descriptionOG={content.lead}
      />
      <JsonLd data={serviceSchema} />
      <JsonLd data={getPricingBreadcrumbSchema(type, canonicalUrl)} />
      <main className={Styles.newbornPricing}>
        <PricingNavigation current={type} />
        <header className={Styles.newbornPricing__hero}>
          <p className={Styles.newbornPricing__eyebrow}>{content.eyebrow}</p>
          <h1>{content.title}</h1>
          <p className={Styles.newbornPricing__lead}>{content.lead}</p>
          <p className={Styles.newbornPricing__styleNote}>
            <strong>Choose your session type.</strong> {content.styleNote}
          </p>
        </header>

        {!isSinglePackage && (
          <section aria-labelledby={`${type}-comparison-title`}>
            <h2 className={Styles.newbornPricing__sectionTitle} id={`${type}-comparison-title`}>
              Quick package comparison
            </h2>
            <div className={Styles.newbornPricing__comparisonWrapper}>
              <table className={Styles.newbornPricing__comparison}>
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Session style</th>
                    <th>Time</th>
                    <th>Images</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {content.packages.map((item) => (
                    <tr key={item.name}>
                      <td>{item.name}</td>
                      <td>{item.style}</td>
                      <td>{item.time}</td>
                      <td>{item.images}</td>
                      <td>{item.priceLabel ?? item.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={Styles.newbornPricing__setupNote}>
              Review the session format, included images, location options, and additional services before booking.
            </p>
          </section>
        )}

        <section aria-labelledby={`${type}-package-title`}>
          <h2 className={Styles.newbornPricing__sectionTitle} id={`${type}-package-title`}>
            {isSinglePackage ? "Your maternity session" : "Explore every package"}
          </h2>
          <div className={Styles.newbornPricing__packages}>
            {content.packages.map((item) =>
              isSinglePackage ? (
                <article className={Styles.package} id={item.id} key={item.name}>
                  <header className={`${Styles.package__summary} ${Styles.package__summary_static}`}>
                    <span className={Styles.package__heading}>
                      <span className={Styles.package__name}>{item.name}</span>
                      <span className={Styles.package__price}>{item.priceLabel ?? item.price}</span>
                    </span>
                  </header>
                  {renderPackageBody(item)}
                </article>
              ) : (
                <details
                  className={Styles.package}
                  id={item.id}
                  key={item.name}
                  open={width > 760 || hash === `#${item.id}`}
                >
                  <summary className={Styles.package__summary}>
                    <span className={Styles.package__heading}>
                      <span className={Styles.package__name}>{item.name}</span>
                      <span className={Styles.package__price}>{item.priceLabel ?? item.price}</span>
                    </span>
                  </summary>
                  {renderPackageBody(item)}
                </details>
              )
            )}
          </div>
        </section>

        <section className={Styles.newbornPricing__policies} aria-label="Booking and privacy information">
          <article className={Styles.policy}>
            <p className={Styles.newbornPricing__eyebrow}>Booking policies</p>
            <h2>Important Session Information</h2>
            <h3>Retainer</h3>
            <p>
              A $100 non-refundable retainer is required to reserve a portrait session date and time. The retainer is
              credited toward the package price. One rescheduling is permitted with at least 48 hours&apos; notice.
            </p>
            <h3>Locations and studio rental</h3>
            <p>
              Portrait sessions take place at one selected location: your home, outdoors, or a professional studio.
              Studio rental is separate and varies by studio. Home suitability and equipment needs are reviewed before
              the session.
            </p>
            <h3>Travel fees</h3>
            <p>
              Travel within the Charlotte area starts at $30 and is calculated based on the session address. Event
              travel within Charlotte and nearby areas is $30. Longer-distance travel is quoted separately.
            </p>
            <h3>Portrait-session image options</h3>
            <ul>
              <li>Additional fully retouched images: $25 each</li>
              <li>All remaining usable unretouched JPEG images from the portrait session: $100</li>
              <li>RAW files are not provided</li>
            </ul>
            <h3>Image selection and delivery</h3>
            <p>
              Test frames, duplicates, missed-focus images, and photographs with closed eyes or unusable expressions are
              not delivered. Portrait galleries are typically delivered within 1–1.5 weeks; event galleries are
              delivered within 1–2 weeks.
            </p>
          </article>
          <article className={`${Styles.policy} ${Styles.policy_private}`}>
            <p className={Styles.newbornPricing__eyebrow}>Privacy options</p>
            <h2>Portfolio-Use Discount &amp; Private Sessions</h2>
            <p className={Styles.policy__pricingNote}>
              <strong>Pricing note.</strong> Portrait package prices include a $200 portfolio-use discount and require a
              signed Model Release allowing selected images to be used in the Alena Lobacheva Photography portfolio,
              website, and social media.
            </p>
            <p>
              Clients may keep a portrait session completely private. No images will be shared publicly, and the regular
              private-session price applies.
            </p>
            <dl className={Styles.policy__privatePrices}>
              {content.privatePrices.map(([name, price]) => (
                <div key={name}>
                  <dt>{name}</dt>
                  <dd>{price}</dd>
                </div>
              ))}
            </dl>
            {type === "family" && (
              <>
                <h3>Small Event Photography</h3>
                <p>
                  Event pricing is separate from the portrait portfolio-use discount. Any public use of event
                  photographs will be discussed separately because events may include multiple guests.
                </p>
              </>
            )}
            <h3>Confidentiality</h3>
            <p>
              When a private portrait session is selected, all photographs remain confidential and will not be published
              by Alena Lobacheva Photography.
            </p>
          </article>
        </section>

        <PricingGuideCenter category={type} />

        <section className={Styles.newbornPricing__cta}>
          <h2>Ready to plan your {type === "baby" ? "baby" : type} session?</h2>
          <p>Have a question before booking? Send me a message and we&apos;ll plan the right experience for you.</p>
          <Link className={Styles.newbornPricing__ctaLink} to="/contact/">
            Contact Alena
          </Link>
        </section>
        <PricingCategoryNavigation current={type} />
      </main>
    </>
  );
};

export default FamilyMaternityPricingPage;
